import { useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useNotificationCount } from '@/hooks/useNotificationCount';
import { useCreateSheet } from '@/context/CreateSheetContext';

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count: unreadCount } = useNotificationCount();
  const { open } = useCreateSheet();

  // Long press logic: 400ms → skip sheet, open last-used studio
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const handleDropPointerDown = (e) => {
    e.preventDefault();
    longPressFiredRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      const last = localStorage.getItem('blvx-lastUsedFormat') || 'moment';
      navigate(`/drop?mode=${last}`);
    }, 400);
  };

  const handleDropPointerUp = () => {
    clearTimeout(pressTimerRef.current);
    if (!longPressFiredRef.current) {
      open();
    }
  };

  const handleDropPointerCancel = () => {
    clearTimeout(pressTimerRef.current);
  };

  const isCookoutActive = location.pathname === '/home' && location.search.includes('tab=cookout');
  const isBlockActive = location.pathname === '/home' && !isCookoutActive;
  const isGCActive = location.pathname.startsWith('/gc');
  const isExploreActive = location.pathname.startsWith('/explore');

  const tabStyle = (active) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    height: '100%',
    gap: '3px',
    cursor: 'pointer',
    position: 'relative',
    border: 'none',
    background: 'none',
    padding: 0,
    textDecoration: 'none',
  });

  const iconStyle = (active) => ({
    fontSize: '19px',
    color: active ? 'var(--cream,#EAE0CC)' : 'var(--ash,#72788A)',
    lineHeight: 1,
    position: 'relative',
  });

  const labelStyle = (active) => ({
    fontFamily: "'Azeret Mono', monospace",
    fontSize: '8px',
    letterSpacing: '0.5px',
    color: active ? 'var(--cream,#EAE0CC)' : 'var(--ash,#72788A)',
    textTransform: 'uppercase',
  });

  const dotStyle = {
    position: 'absolute',
    bottom: '-4px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '4px',
    height: '4px',
    background: 'var(--fire,#F07B1D)',
    borderRadius: '50%',
  };

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 50,
    }} className="md:hidden">
      {/* Fire accent line */}
      <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(240,123,29,0.4),transparent)' }} />
      <div style={{
        background: 'rgba(5,6,8,0.96)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: '9px',
        paddingBottom: 'env(safe-area-inset-bottom,0px)',
        paddingLeft: 'env(safe-area-inset-left,0px)',
        paddingRight: 'env(safe-area-inset-right,0px)',
        height: '68px',
      }}>

        {/* Block */}
        <Link to="/home" style={tabStyle(isBlockActive)} data-testid="nav-block">
          <span style={iconStyle(isBlockActive)}>
            ⬛
            {isBlockActive && <span style={dotStyle} />}
          </span>
          <span style={labelStyle(isBlockActive)}>Block</span>
        </Link>

        {/* Cookout */}
        <Link to="/home?tab=cookout" style={tabStyle(isCookoutActive)} data-testid="nav-cookout">
          <span style={iconStyle(isCookoutActive)}>
            🍽
            {isCookoutActive && <span style={{ ...dotStyle, background: 'var(--jade,#18B574)' }} />}
          </span>
          <span style={labelStyle(isCookoutActive)}>Cookout</span>
        </Link>

        {/* Drop — center elevated circle */}
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer', marginTop: '-14px', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' }}
          onPointerDown={handleDropPointerDown}
          onPointerUp={handleDropPointerUp}
          onPointerLeave={handleDropPointerCancel}
          onPointerCancel={handleDropPointerCancel}
          data-testid="nav-drop"
        >
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'var(--fire,#F07B1D)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            color: '#050608',
            fontWeight: 700,
            boxShadow: '0 4px 18px rgba(240,123,29,0.5), 0 0 0 4px rgba(240,123,29,0.1)',
          }}>
            ＋
          </div>
          <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '7px', letterSpacing: '0.5px', color: 'var(--ash,#72788A)', textTransform: 'uppercase' }}>
            Drop
          </span>
        </div>

        {/* GC */}
        <Link to="/gc" style={tabStyle(isGCActive)} data-testid="nav-gc">
          <span style={{ ...iconStyle(isGCActive), position: 'relative' }}>
            💬
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-2px',
                right: '3px',
                width: '14px',
                height: '14px',
                background: '#D44F2E',
                borderRadius: '50%',
                border: '2px solid #050608',
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '8px',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            {isGCActive && <span style={dotStyle} />}
          </span>
          <span style={labelStyle(isGCActive)}>GC</span>
        </Link>

        {/* Explore */}
        <Link to="/explore" style={tabStyle(isExploreActive)} data-testid="nav-explore">
          <span style={iconStyle(isExploreActive)}>
            🔍
            {isExploreActive && <span style={dotStyle} />}
          </span>
          <span style={labelStyle(isExploreActive)}>Explore</span>
        </Link>

      </div>
    </nav>
  );
};
